import { describeRecurrence, type CheckInTemplate, type Track } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, IconButton } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { useToast } from '../../components/Toast.js'
import { checkInColour, paletteVar } from '../../charts/colours.js'
import {
  HeaderLink,
  Icon,
  ICONS,
  Page,
  Section,
  SubHeader,
  Swatch,
  TitleBlock,
} from '../../components/Layout.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Templates.module.css'

/**
 * Check-ins: named sets of tracks recorded together (T-303).
 *
 * Their order here is the order of the buttons on the home screen, so the
 * most-used check-in can be put first — which matters more than it sounds,
 * because this is the screen that decides whether logging is one tap or three.
 */
export function TemplateListScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<CheckInTemplate[] | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [logged, setLogged] = useState<Map<string, number>>(new Map())
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState(false)
  /** Each check-in's reminder, in words, for those that have a live one. */
  const [schedules, setSchedules] = useState<ReadonlyMap<string, string>>(new Map())

  const reload = useCallback(async () => {
    const [all, allTracks, counts, reminders] = await Promise.all([
      repos.templates.list({ includeArchived: true }),
      repos.tracks.list({ includeArchived: true }),
      repos.templates.countsByTemplate(),
      repos.reminders.list(),
    ])
    setSchedules(
      new Map(
        reminders
          .filter((reminder) => reminder.enabled && reminder.templateId !== undefined)
          .map((reminder) => [
            reminder.templateId ?? '',
            describeRecurrence(reminder.recurrence),
          ]),
      ),
    )
    setTemplates(all)
    setTracks(allTracks)
    setLogged(counts)
  }, [repos])

  useEffect(() => {
    void reload()
  }, [reload])

  const active = (templates ?? []).filter((t) => !t.archived)
  const archived = (templates ?? []).filter((t) => t.archived)

  const byId = new Map(tracks.map((track) => [track.id, track]))

  /** Tracks that were archived or deleted after the template was written. */
  function staleCount(template: CheckInTemplate): number {
    return template.trackIds.filter((id) => byId.get(id)?.archived !== false).length
  }

  /**
   * Whether starting this check-in would produce a flow with anything in it.
   *
   * A template whose tracks have all been archived resolves to no steps, and
   * offering to log it would open a flow with nothing to answer and no way
   * forward. Editing it is the useful action there, and it is the one offered.
   */
  function isLoggable(template: CheckInTemplate): boolean {
    return template.trackIds.some((id) => byId.get(id)?.archived === false)
  }

  async function move(from: number, to: number) {
    if (to < 0 || to >= active.length) {
      return
    }
    const order = active.map((t) => t.id)
    const [moved] = order.splice(from, 1)
    if (moved === undefined) {
      return
    }
    order.splice(to, 0, moved)
    await repos.templates.reorder([...order, ...archived.map((t) => t.id)])
    await reload()
  }

  async function duplicate(template: CheckInTemplate) {
    // Variants differ by a track or two, so copying is the normal way to make
    // the second one.
    const result = await repos.templates.duplicate(template.id, `${template.name} copy`)
    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'Could not copy.', tone: 'danger' })
      return
    }
    await reload()
    void navigate(`/templates/${result.value.id}`)
  }

  async function remove(template: CheckInTemplate) {
    // Unlike a track, deleting a template destroys nothing: past entries keep
    // their templateId and stay attributable (T-107).
    await repos.templates.remove(template.id)
    await reload()
    show({ message: `Deleted ${template.name}. Entries it created are unaffected.` })
  }

  if (templates === null) {
    return (
      <Page>
        <SubHeader back={{ to: '/settings', label: 'Settings' }} />
        <h1>Check-ins</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  function loggedText(template: CheckInTemplate): string {
    const count = logged.get(template.id) ?? 0
    return count === 0
      ? 'Never logged'
      : count === 1
        ? 'Logged once'
        : `Logged ${String(count)} times`
  }

  return (
    <Page>
      <SubHeader
        back={{ to: '/settings', label: 'Settings' }}
        action={
          /*
            A check-in needs a track to ask about. Shown as text rather than
            hidden when there are none, so the screen still says what it is
            for; the empty state below says what to do instead.
          */
          tracks.length === 0 ? undefined : (
            <HeaderLink to="/templates/new" icon={ICONS.plus}>
              New check-in
            </HeaderLink>
          )
        }
      />
      <TitleBlock
        title="Check-ins"
        lead="A few tracks asked together, one question at a time."
      />

      {active.length === 0 ? (
        <EmptyState
          title="No check-ins yet"
          body={
            tracks.length === 0
              ? 'A check-in records several tracks together, so you need at least one track first.'
              : 'A check-in is a named set of tracks you record together — "Before eating", "Bedtime". They become the buttons on your home screen, which is what makes logging one tap instead of five.'
          }
          action={
            tracks.length === 0 ? (
              <Button variant="primary" onClick={() => void navigate('/tracks')}>
                Set up a track
              </Button>
            ) : undefined
          }
        />
      ) : editing ? (
        <Section
          id="order"
          label="Order"
          aside={
            <button
              type="button"
              className={styles['modeButton']}
              aria-pressed={true}
              onClick={() => setEditing(false)}
            >
              Done
            </button>
          }
          note="Order sets the order of the buttons on Home."
        >
          {active.map((template, index) => (
            <div key={template.id} className={styles['editRow']}>
              <span className={styles['moves']}>
                <IconButton
                  label={`Move ${template.name} up`}
                  disabled={index === 0}
                  onClick={() => void move(index, index - 1)}
                >
                  <Icon path="M6 15l6-6 6 6" />
                </IconButton>
                <IconButton
                  label={`Move ${template.name} down`}
                  disabled={index === active.length - 1}
                  onClick={() => void move(index, index + 1)}
                >
                  <Icon path="M6 9l6 6 6-6" />
                </IconButton>
              </span>
              <Swatch colour={checkInColour(template)} size={10} />
              <span className={styles['editName']}>{template.name}</span>
              <IconButton
                label={`Duplicate ${template.name}`}
                onClick={() => void duplicate(template)}
              >
                <Icon path="M8 8V4h12v12h-4M4 8h12v12H4z" />
              </IconButton>
              <IconButton
                label={`Archive ${template.name}`}
                onClick={() => void repos.templates.archive(template.id).then(reload)}
              >
                <Icon path="M4 7h16v13H4zM4 7l2-3h12l2 3M10 11h4" />
              </IconButton>
              <IconButton
                label={`Delete ${template.name}`}
                onClick={() => void remove(template)}
              >
                <Icon path="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />
              </IconButton>
            </div>
          ))}
        </Section>
      ) : (
        <>
          <div className={styles['bands']} data-bleed>
            {active.map((template) => {
              const stale = staleCount(template)
              const colour = checkInColour(template)
              const schedule = schedules.get(template.id)
              return (
                <div
                  key={template.id}
                  className={styles['band']}
                  style={{ background: paletteVar(colour, 'tint') }}
                >
                  {/*
                    Tapping a check-in **starts it** (T-908). It is a thing you
                    do far more often than a thing you edit, and the editor
                    keeps its own way in beside it — which is the reachability
                    trap `shell.spec.ts` exists to catch.
                  */}
                  <button
                    type="button"
                    className={styles['bandBody']}
                    /*
                      Named for what it does. Left to concatenation the name
                      would be everything on the band read out as though it
                      were the label. It still opens with the visible name,
                      which is what a speech-input user will say (WCAG 2.5.3).
                    */
                    aria-label={`Log ${template.name}`}
                    disabled={!isLoggable(template)}
                    onClick={() => void navigate(`/log/${template.id}`)}
                  >
                    <span className={styles['bandHead']}>
                      <span className={styles['bandName']}>
                        <Swatch colour={colour} size={10} />
                        {template.name}
                      </span>
                      {schedule !== undefined && (
                        <span
                          className={styles['schedule']}
                          style={{ color: paletteVar(colour, 'deep') }}
                        >
                          {schedule}
                        </span>
                      )}
                    </span>
                    <span className={styles['bandTracks']}>
                      {template.trackIds
                        .map((id) => byId.get(id))
                        .filter((track): track is Track => track !== undefined)
                        .map((track) => track.name)
                        .join(' · ')}
                    </span>
                    <span className={styles['meta']}>
                      <span>{loggedText(template)}</span>
                      {stale > 0 && (
                        <span className={styles['warning']}>
                          {stale === 1
                            ? '1 needs attention'
                            : `${String(stale)} need attention`}
                        </span>
                      )}
                    </span>
                  </button>
                  <IconButton
                    label={`Edit ${template.name}`}
                    onClick={() => void navigate(`/templates/${template.id}`)}
                  >
                    <Icon path="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" />
                  </IconButton>
                </div>
              )
            })}
          </div>
          <div className={styles['listFoot']}>
            <p className={styles['note']}>
              The colour of a check-in shows on Home, the log flow and the timeline, so
              you can spot it at a glance.
            </p>
            <button
              type="button"
              className={styles['modeButton']}
              aria-pressed={false}
              onClick={() => setEditing(true)}
            >
              Edit list
            </button>
          </div>
        </>
      )}

      {archived.length > 0 && (
        <div className={styles['archived']} data-bleed>
          <button
            type="button"
            className={styles['disclosure']}
            aria-expanded={showArchived}
            onClick={() => setShowArchived((open) => !open)}
          >
            <span className={styles['disclosureLabel']}>Archived check-ins</span>
            <span className={styles['disclosureDetail']}>{archived.length}</span>
            <Icon
              path={ICONS.chevronRight}
              className={`${styles['chevron']} ${showArchived ? styles['chevronOpen'] : ''}`}
            />
          </button>
          {showArchived &&
            archived.map((template) => (
              <div key={template.id} className={styles['editRow']}>
                <Swatch colour={checkInColour(template)} size={10} />
                <span className={styles['editName']}>{template.name}</span>
                <IconButton
                  label={`Restore ${template.name}`}
                  onClick={() =>
                    void repos.templates
                      .update(template.id, { archived: false })
                      .then(reload)
                  }
                >
                  <Icon path="M5 12a7 7 0 1 1 2 5M5 7v5h5" />
                </IconButton>
              </div>
            ))}
        </div>
      )}
    </Page>
  )
}
